import { useCallback, useState } from 'react';
import type { Group, GroupCreationRequest } from '@penticton-dev-events/shared';

import { useFirebaseServices } from '../../../app/providers/FirebaseProvider';
import { useAuth } from '../../../app/providers/AuthProvider';
import { useIsPlatformAdmin } from '../../auth/hooks/usePermissions';
import { createGroup } from '../api/groupRepository';
import { createGroupCreationRequest } from '../api/groupCreationRequestRepository';

export type CreateGroupOrRequestPayload = {
  readonly name: string;
  readonly slug?: string;
  readonly summary?: string;
  readonly autoAcceptMembers: boolean;
};

export type CreateGroupOrRequestResult =
  | { type: 'group'; data: Group }
  | { type: 'request'; data: GroupCreationRequest };

export type UseCreateGroupOrRequestReturn = {
  readonly createGroupOrRequest: (
    payload: CreateGroupOrRequestPayload,
  ) => Promise<CreateGroupOrRequestResult>;
  readonly isCreating: boolean;
  readonly error: Error | null;
  readonly clearError: () => void;
};

/**
 * Smart group creation hook that adapts based on user permissions
 *
 * Flow:
 * - Platform admins: Create group directly (bypasses approval workflow)
 * - Regular users: Create group creation request (requires admin approval)
 *
 * This provides a unified interface for group creation while implementing
 * the RBAC approval workflow seamlessly.
 *
 * @returns Object containing createGroupOrRequest method and state
 *
 * @example
 * ```tsx
 * const { createGroupOrRequest, isCreating, error } = useCreateGroupOrRequest();
 *
 * const handleSubmit = async (formData) => {
 *   try {
 *     const result = await createGroupOrRequest(formData);
 *     if (result.type === 'group') {
 *       // Admin: Group created directly
 *       navigate(`/groups/${result.data.id}`);
 *     } else {
 *       // Regular user: Request submitted
 *       showMessage('Your request has been submitted for approval');
 *     }
 *   } catch (err) {
 *     // Error handled in hook state
 *   }
 * };
 * ```
 */
export const useCreateGroupOrRequest = (): UseCreateGroupOrRequestReturn => {
  const { firestore } = useFirebaseServices();
  const { user, profile } = useAuth();
  const isPlatformAdmin = useIsPlatformAdmin();

  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const createGroupOrRequest = useCallback(
    async (payload: CreateGroupOrRequestPayload): Promise<CreateGroupOrRequestResult> => {
      // Ensure user is authenticated
      if (!user || !profile) {
        const authError = new Error('You must be signed in to create a group');
        setError(authError);
        throw authError;
      }

      // Block suspended users
      if (profile.accountStatus === 'suspended') {
        const suspendedError = new Error('Your account has been suspended. You cannot create groups or requests.');
        setError(suspendedError);
        throw suspendedError;
      }

      // Block pending users (optional - you may want to allow pending users to submit requests)
      if (profile.accountStatus === 'pending') {
        const pendingError = new Error('Your account is pending approval. You cannot create groups or requests yet.');
        setError(pendingError);
        throw pendingError;
      }

      setIsCreating(true);
      setError(null);

      try {
        // Platform admins create groups directly
        if (isPlatformAdmin) {
          const group = await createGroup(firestore, {
            name: payload.name,
            slug: payload.slug || '', // Will be generated from name if empty
            summary: payload.summary,
            autoAcceptMembers: payload.autoAcceptMembers,
            organizerUid: user.uid,
            organizerName: profile.displayName || 'Unknown Organizer',
            approvalStatus: 'approved', // Platform admins bypass approval
            approvedBy: user.uid, // Admin who created it
            approvedAt: new Date().toISOString(), // Approved immediately
          });

          return { type: 'group', data: group };
        }

        // Regular users create group creation requests
        const request = await createGroupCreationRequest(firestore, {
          name: payload.name,
          slug: payload.slug || '', // Will be generated from name if empty
          summary: payload.summary,
          autoAcceptMembers: payload.autoAcceptMembers,
          requesterUid: user.uid,
          requesterName: profile.displayName || 'Unknown User',
          requesterEmail: profile.email || `${user.uid}@users.noreply`,
        });

        return { type: 'request', data: request };
      } catch (creationError) {
        const errorMessage =
          creationError instanceof Error
            ? creationError
            : new Error('Failed to create group or request');
        setError(errorMessage);
        throw errorMessage;
      } finally {
        setIsCreating(false);
      }
    },
    [firestore, user, profile, isPlatformAdmin],
  );

  return {
    createGroupOrRequest,
    isCreating,
    error,
    clearError,
  };
};
